import classnames from 'classnames';
import React from 'react';

import {FormikContextType, FormikHelpers, useFormikContext} from 'formik';
import Icon from '../../../shared/components/icon';
import Loading from '../../../shared/components/loading';
import ProgressBar from '../../../shared/components/progress-bar';
import {StepPosition} from '../../types/form';

import styles from './onboarding.module.scss';

export interface OnboardingProgressBarProps<Values> {
  decrementStep: (helpers?: FormikHelpers<Values>) => Promise<void> | void;
  stepPosition: StepPosition;
  stepCount: number;
  isSubmitting: boolean;
  hasErrors: boolean;
  invalid: boolean;
  isLoading: boolean;
  isControlledStep: boolean;
  onControlledSubmit: (
    form: FormikContextType<Values>,
    stepPosition: StepPosition
  ) => Promise<void>;
}

const OnboardingProgressBar = <Values,>({
  decrementStep,
  stepPosition,
  stepCount,
  isSubmitting,
  hasErrors,
  invalid,
  isLoading,
  isControlledStep,
  onControlledSubmit,
}: OnboardingProgressBarProps<Values>): React.ReactElement => {
  const primaryDisabled = invalid || isSubmitting || hasErrors;
  const form = useFormikContext<Values>();

  const primaryActionLoading = isSubmitting || isLoading;
  const isControlledSubmit = (): void => {
    onControlledSubmit(form, stepPosition);
  };

  const fillAmount = ((Math.min(stepPosition.index, stepCount) / stepCount) * 100).toFixed(0) + '%';

  return (
    <div className={styles.progressBarContainer} data-testid="onboarding-progress-bar">
      <Icon
        className={classnames(styles.onboardingProgressBarBackwardsIcon, {
          [styles.hide]: stepPosition.index === 0,
        })}
        iconType="chevron-left"
        onClick={decrementStep}
        testid="onboarding-progress-bar-backwards-icon"
      />
      <ProgressBar fillAmount={fillAmount} className={styles.progressBar} color="blue-gradient" />
      {primaryActionLoading ? (
        <Loading className={styles.loading} />
      ) : (
        <button
          type={isControlledStep ? 'button' : 'submit'}
          className={styles.onboardingSubmitButton}
          data-cy="onboarding-next"
          data-testid="onboarding-progress-bar-submit-button"
          onClick={isControlledStep ? isControlledSubmit : undefined}
        >
          <Icon
            className={classnames(
              styles.onboardingProgressBarForwardsIcon,
              primaryDisabled ? styles.disabled : ''
            )}
            iconType="chevron-right"
            testid="onboarding-progress-bar-forwards-icon"
          />
        </button>
      )}
    </div>
  );
};

export default OnboardingProgressBar;
